import { useEffect, useMemo, useState } from 'react';

import { useSharedThemeStore } from '../../stores/use-shared-theme-store';

import {
  ThemeProviderContext,
  ThemeProviderProps,
  ThemeProviderState,
  Theme,
  ThemeName,
} from './theme-provider-context';

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'heartplus-theme',
  defaultThemeName = 'default',
  themeNameStorageKey = 'heartplus-theme-name',
  availableThemes = [
    'default',
    'starry-night',
    'violet-bloom',
    'neo-brutalism',
    'bubble-gum',
  ],
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    try {
      return (
        (localStorage.getItem(themeNameStorageKey) as ThemeName) ||
        defaultThemeName
      );
    } catch {
      return defaultThemeName;
    }
  });

  const resolvedTheme: 'dark' | 'light' = useMemo(() => {
    if (typeof window === 'undefined') return 'light';
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return theme;
  }, [theme]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);

    // Remove any previously set theme-* classes
    const classesToRemove: string[] = [];
    root.classList.forEach((cls) => {
      if (cls.startsWith('theme-')) classesToRemove.push(cls);
    });
    classesToRemove.forEach((cls) => root.classList.remove(cls));

    // Apply current theme name class unless it's the default theme
    const toKebab = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    if (themeName && toKebab(themeName) !== 'default') {
      root.classList.add(`theme-${toKebab(themeName)}`);
    }
  }, [resolvedTheme, themeName]);

  useEffect(() => {
    useSharedThemeStore.getState().setTheme({
      colorMode: resolvedTheme,
      themeName,
    });
  }, [resolvedTheme, themeName]);

  const value: ThemeProviderState = {
    theme,
    setTheme: (nextTheme: Theme) => {
      try {
        localStorage.setItem(storageKey, nextTheme);
      } catch {
        // ignore
      }
      setTheme(nextTheme);
    },
    resolvedTheme,
    themeName,
    setThemeName: (nextName: ThemeName) => {
      try {
        localStorage.setItem(themeNameStorageKey, nextName);
      } catch {
        // ignore
      }
      setThemeNameState(nextName);
    },
    availableThemes,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
